/**
 * Aplica um efeito de fundo na trilha da câmera com o `BackgroundProcessor` do LiveKit (`@livekit/track-processors`),
 * sem React. O pacote (e o MediaPipe que ele traz) só é baixado na primeira vez que alguém escolhe um efeito.
 *
 * Uma sessão cuida de uma trilha por vez: trocar de efeito na mesma trilha só muda o modo do processador (sem
 * quadro piscando); trilha nova (a câmera foi republicada) ganha um processador novo. O SDK reinicia o processador
 * sozinho quando a câmera é desligada e ligada ou trocada de dispositivo.
 *
 * O MediaPipe tenta a GPU; sem ela (ou sem suporte), tenta de novo na CPU, que é mais lenta mas funciona.
 */
import { BACKGROUND_ASSET_PATHS } from './backgroundAssets';

export function createBackgroundSession({ loadModule = () => import('@livekit/track-processors'), assetPaths = BACKGROUND_ASSET_PATHS } = {}) {
    let entry = null; // { track, processor }

    async function attach(track, mode) {
        const { BackgroundProcessor } = await loadModule();
        const create = (delegate) => BackgroundProcessor({ ...mode, assetPaths, ...(delegate ? { segmenterOptions: { delegate } } : {}) });
        let processor = create();
        try {
            await track.setProcessor(processor);
        } catch {
            // A inicialização falhou antes de a trilha trocar de processador: a trilha segue como estava.
            await Promise.resolve(processor.destroy?.()).catch(() => {});
            processor = create('CPU');
            await track.setProcessor(processor);
        }
        entry = { track, processor };
    }

    /** Liga (ou troca) o efeito. `option.mode` nulo é "sem efeito". */
    async function apply(track, option) {
        if (!option?.mode) return clear(track);
        if (entry && entry.track === track && track.getProcessor?.() === entry.processor) {
            await entry.processor.switchTo(option.mode);
            return undefined;
        }
        return attach(track, option.mode);
    }

    /** Tira o efeito e devolve a imagem original da câmera. */
    async function clear(track) {
        entry = null;
        if (track?.getProcessor?.()) await track.stopProcessor();
    }

    return { apply, clear, hasEffect: () => entry !== null };
}
